import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useEmployee } from '~/data/employee';
import { useAllEmployees } from '~/data/employees';
import {
  getCountryByLegalEntity,
  useAllLegalEntities,
} from '~/data/legalEntity';
import { BonusProposalProps } from '~/data/proposals';
import { BonusProgramConfig } from '~/data/rewarding/types';
import { useGetBonusProgramsData } from '~/data/rewarding/useGetBonusPrograms';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import {
  ProposalFormPropsMessage,
  ProposalFormPropsMessageContext,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/Common';
import { EmployeeProposalsLayoutProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/EmployeeProposalsLayout/component';

export type BonusProposalFormProps = {
  legalEntityCountryForUser: string;
  bonusProgramConfigs: BonusProgramConfig[];
  messages: ProposalFormPropsMessage[];
};

type UseBonusHoursLayoutOutput = {
  newProposal?: BonusProposalProps;
  formProps: BonusProposalFormProps;
  isLoading: boolean;
};

export const useBonusProposalsLayout = (
  props: EmployeeProposalsLayoutProps<BonusProposalProps>,
): UseBonusHoursLayoutOutput => {
  const { newProposal } = props;
  const { t } = useTranslation();

  // Get EmployeeId
  const { selectedEmployeeId } = useSelectedEmployeeId();
  const employeeId = newProposal?.targetId || selectedEmployeeId;

  // Get EmploymentId
  const { allEmployees } = useAllEmployees();
  const employmentId = useMemo(
    () => allEmployees?.[employeeId]?.fEmpEmplId || -1,
    [allEmployees, employeeId],
  );

  const { data: bonusProgramConfigs, isLoading: isLoadingBonusProgramsData } =
    useGetBonusProgramsData({ employeeId, employmentId });

  const bonusProgramConfigsAvailable = !!bonusProgramConfigs?.length;

  const newProposalEnabled = bonusProgramConfigsAvailable;

  // Resolve legalEntityCountryForUser
  const { employee, isLoading: isLoadingEmployee } = useEmployee();

  const {
    legalEntities,
    isLoading: isLoadingLegalEntities,
    hasData: legalEntityHasData,
  } = useAllLegalEntities();

  const legalEntityCountryForUser = useMemo(() => {
    const { empBasicData, empPosition, empPositionPending } = employee;

    const legalEntityId =
      ['NEW', 'PENDING'].includes(empBasicData.fEmpStatus) && empPositionPending
        ? empPositionPending.fEmpLegalEntity
        : empPosition.fEmpLegalEntity;

    return getCountryByLegalEntity(legalEntityId, legalEntities);
  }, [legalEntities, employee]);

  const messages: ProposalFormPropsMessage[] = [
    {
      show: !bonusProgramConfigsAvailable,
      payload: {
        context: ProposalFormPropsMessageContext.NewProposal,
        message: `${t('createNewProposalUnavailable')}: ${t(
          'noBonusProgramsConfigured',
        )}`,
      },
    },
  ]
    .filter((m) => m.show)
    .map((m) => m.payload);

  const formProps: BonusProposalFormProps = {
    legalEntityCountryForUser,
    bonusProgramConfigs,
    messages,
  };

  const isLoading =
    isLoadingEmployee ||
    isLoadingBonusProgramsData ||
    (isLoadingLegalEntities && !legalEntityHasData);

  return {
    newProposal: newProposalEnabled ? newProposal : undefined,
    formProps,
    isLoading,
  };
};
